/**
 * Alsunu — animated <details> panels (buy-box accordion + product FAQ).
 *
 * Native <details> snaps open and shut: the content below jumps a full panel
 * height in one frame. This animates the panel's height from its measured
 * size (never `auto`), fades the content in, and closes with the same motion
 * in reverse.
 *
 * - Progressive enhancement: without JS, <details> works natively.
 * - Interruptible: a second tap mid-animation reverses from the current
 *   height instead of restarting.
 * - Layout cost is limited to the one panel for ~250ms, and it only ever
 *   follows a tap, so the movement counts as user-initiated (no CLS).
 * - Reduced motion: falls back to the native instant toggle.
 * - One delegated listener for every panel on the page.
 */
(() => {
  if (window.__alsAccordion) return;
  window.__alsAccordion = true;

  const ITEM = 'details.als-acc__i, details.als-faq__i';
  const PANEL = ':scope > .als-acc__panel, :scope > .als-faq__a-wrap';
  const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
  const OPEN_MS = 260;
  const CLOSE_MS = 200;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {WeakMap<HTMLDetailsElement, Animation>} */
  const running = new WeakMap();

  const finish = (details, panel, open) => {
    running.delete(details);
    details.open = open;
    details.dataset.alsState = open ? 'open' : 'closed';
    panel.style.height = '';
    panel.style.overflow = '';
  };

  const toggle = (details, panel) => {
    const current = running.get(details);
    const state = details.dataset.alsState || (details.open ? 'open' : 'closed');
    const opening = state === 'closed' || state === 'closing';
    const from = details.open ? panel.getBoundingClientRect().height : 0;

    if (current) current.cancel();

    details.dataset.alsState = opening ? 'opening' : 'closing';
    panel.style.overflow = 'hidden';

    if (opening) details.open = true;
    const to = opening ? panel.scrollHeight : 0;
    const distance = Math.abs(to - from);
    const full = opening ? OPEN_MS : CLOSE_MS;
    // A reversal part-way through covers less ground, so it takes less time.
    const duration = Math.max(120, Math.round(full * Math.min(1, distance / Math.max(panel.scrollHeight, 1))));

    const animation = panel.animate(
      { height: [`${from}px`, `${to}px`] },
      { duration, easing: EASE }
    );
    running.set(details, animation);

    const inner = panel.firstElementChild;
    if (inner && opening) {
      inner.animate(
        { opacity: [0, 1], transform: ['translateY(-6px)', 'translateY(0)'] },
        { duration: duration + 60, easing: EASE }
      );
    }

    animation.onfinish = () => finish(details, panel, opening);
  };

  document.addEventListener('click', (event) => {
    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    if (!summary) return;
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement) || !details.matches(ITEM)) return;
    if (reduceMotion.matches || typeof details.animate !== 'function') return;

    const panel = details.querySelector(PANEL);
    if (!panel) return;

    event.preventDefault();
    toggle(details, panel);
  });

  // Find-in-page and #fragment navigation open a panel without a click; keep
  // the state flag in sync so the next tap closes it.
  document.addEventListener(
    'toggle',
    (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches(ITEM)) return;
      if (running.has(details)) return;
      details.dataset.alsState = details.open ? 'open' : 'closed';
    },
    true
  );
})();
